<template>

  <Head title="Jenis Dokumen" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div class="min-h-screen bg-gray-50">
        <div class="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <Breadcrumbs :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Jenis Dokumen', href: route('jenisdokumen.index') },
          ]" />

          <!-- Header Section -->
          <div class="mb-8">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 class="text-3xl font-bold text-gray-900">Manajemen Jenis Dokumen</h1>
                <p class="mt-2 text-lg text-gray-600">Kelola data jenis dokumen dalam sistem</p>
              </div>
            </div>
          </div>

          <!-- Form Input Section -->
          <div class="mb-8 bg-white border border-gray-200 shadow-lg rounded-xl" id="jenis-dokumen-form">
            <div
              class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-green-500 to-blue-600 rounded-t-xl sm:px-6 sm:py-4">
              <h2 class="flex items-center text-lg font-bold text-white sm:text-xl">
                <i class="mr-2 fa-solid fa-plus-circle sm:mr-3"></i>
                {{ form.id ? 'Edit Jenis Dokumen' : 'Tambah Jenis Dokumen Baru' }}
              </h2>
            </div>
            <div class="p-4 sm:p-6">
              <form @submit.prevent="submitForm" class="space-y-4">
                <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
                  <div class="lg:col-span-1">
                    <label for="nama" class="block mb-2 text-sm font-medium text-gray-700">
                      <i class="mr-2 text-gray-500 fa-solid fa-file"></i>
                      Nama Jenis Dokumen
                    </label>
                    <input id="nama" v-model="form.nama" type="text" required
                      placeholder="Masukkan nama jenis dokumen..."
                      class="w-full px-4 py-3 text-sm transition-colors duration-200 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      :class="{ 'border-red-500 focus:ring-red-500 focus:border-red-500': form.errors.nama }" />
                    <div v-if="form.errors.nama" class="mt-1 text-sm text-red-600">
                      <i class="mr-1 fa-solid fa-exclamation-circle"></i>
                      {{ form.errors.nama }}
                    </div>
                  </div>

                  <div class="lg:col-span-1">
                    <label for="deskripsi" class="block mb-2 text-sm font-medium text-gray-700">
                      <i class="mr-2 text-gray-500 fa-solid fa-align-left"></i>
                      Deskripsi (Opsional)
                    </label>
                    <textarea id="deskripsi" v-model="form.deskripsi" rows="1"
                      placeholder="Masukkan deskripsi singkat..."
                      class="w-full px-4 py-3 text-sm transition-colors duration-200 border border-gray-300 rounded-lg resize-y focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"></textarea>
                    <div v-if="form.errors.deskripsi" class="mt-1 text-sm text-red-600">
                      <i class="mr-1 fa-solid fa-exclamation-circle"></i>
                      {{ form.errors.deskripsi }}
                    </div>
                  </div>

                  <div class="flex items-end">
                    <div class="flex w-full space-x-2">
                      <button type="submit" :disabled="form.processing"
                        class="inline-flex items-center justify-center flex-1 px-4 py-3 text-sm font-semibold text-white transition-colors duration-200 bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
                        <i v-if="form.processing" class="mr-2 fa-solid fa-spinner fa-spin"></i>
                        <i v-else class="mr-2 fa-solid fa-save"></i>
                        <span class="hidden sm:inline">{{ form.processing ? 'Menyimpan...' : form.id ? 'Update' :
                          'Simpan' }}</span>
                        <span class="sm:hidden">{{ form.processing ? '...' : form.id ? 'Edit' : '+' }}</span>
                      </button>
                      <button v-if="form.id" type="button" @click="cancelEdit"
                        class="px-4 py-3 text-sm font-medium text-gray-700 transition-colors duration-200 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                        <i class="mr-1 fa-solid fa-times"></i>
                        <span class="hidden sm:inline">Batal</span>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <!-- Main Content Card -->
          <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
            <div
              class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4">
              <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <h2 class="flex items-center text-lg font-bold text-white sm:text-xl">
                  <i class="mr-2 fa-solid fa-file sm:mr-3"></i>
                  Data Jenis Dokumen
                </h2>
                <!-- Search Input -->
                <div class="relative w-full mt-3 sm:mt-0 sm:w-80">
                  <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input v-model="search" type="text" autocomplete="off" placeholder="Cari jenis dokumen..."
                    class="w-full py-2 pl-10 pr-4 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-white focus:border-white placeholder:text-gray-200/70" />
                </div>
              </div>
            </div>

            <div class="p-4 sm:p-6">
              <!-- Desktop Table -->
              <div class="hidden overflow-hidden border border-gray-200 rounded-lg lg:block">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th scope="col"
                        class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        <div class="flex items-center space-x-1">
                          <i class="text-gray-400 fa-solid fa-hashtag"></i>
                          <span>No</span>
                        </div>
                      </th>
                      <th scope="col"
                        class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        <div class="flex items-center space-x-1">
                          <i class="text-gray-400 fa-solid fa-file"></i>
                          <span>Nama</span>
                        </div>
                      </th>
                      <th scope="col"
                        class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        <div class="flex items-center space-x-1">
                          <i class="text-gray-400 fa-solid fa-align-left"></i>
                          <span>Deskripsi</span>
                        </div>
                      </th>
                      <th scope="col"
                        class="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase">
                        <div class="flex items-center justify-center space-x-1">
                          <i class="text-gray-400 fa-solid fa-cogs"></i>
                          <span>Aksi</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-if="!jenisdoks.data || jenisdoks.data.length === 0">
                      <td colspan="4" class="px-6 py-8 text-center text-gray-500">
                        <div class="flex flex-col items-center">
                          <i class="mb-3 text-4xl text-gray-300 fa-solid fa-file"></i>
                          <p class="text-lg font-medium">Belum ada data jenis dokumen</p>
                          <p class="text-sm">Gunakan form di atas untuk menambah data baru</p>
                        </div>
                      </td>
                    </tr>
                    <tr v-for="(row, index) in jenisdoks.data" :key="row.id"
                      class="transition-colors duration-150 hover:bg-gray-50">
                      <td class="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">{{ index + 1 }}</td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        <div class="flex items-center">
                          <div class="flex-shrink-0">
                            <div class="flex items-center justify-center w-8 h-8 bg-indigo-100 rounded-full">
                              <i class="text-sm text-indigo-600 fa-solid fa-file"></i>
                            </div>
                          </div>
                          <div class="ml-3">
                            <div class="text-sm font-medium text-gray-900">{{ row.nama }}</div>
                          </div>
                        </div>
                      </td>
                      <td class="px-6 py-4 text-sm text-gray-600">{{ row.deskripsi }}</td>
                      <td class="px-6 py-4 text-center whitespace-nowrap">
                        <div class="flex items-center justify-center space-x-2">
                          <button @click="editJenisDokumen(row)"
                            class="inline-flex items-center px-3 py-2 text-xs font-medium text-indigo-700 transition-colors duration-200 bg-indigo-100 border border-transparent rounded-lg hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            title="Edit Jenis Dokumen">
                            <i class="mr-1 fa-solid fa-edit"></i>
                            Edit
                          </button>
                          <button @click="deleteJenisDokumen(row.id, row.nama)"
                            class="inline-flex items-center px-3 py-2 text-xs font-medium text-red-700 transition-colors duration-200 bg-red-100 border border-transparent rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            title="Hapus Jenis Dokumen">
                            <i class="mr-1 fa-solid fa-trash"></i>
                            Hapus
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Mobile Cards -->
              <div class="space-y-4 lg:hidden">
                <div v-if="!jenisdoks.data || jenisdoks.data.length === 0" class="py-8 text-center text-gray-500">
                  <div class="flex flex-col items-center">
                    <i class="mb-3 text-4xl text-gray-300 fa-solid fa-file"></i>
                    <p class="text-lg font-medium">Belum ada data jenis dokumen</p>
                    <p class="text-sm">Gunakan form di atas untuk menambah data baru</p>
                  </div>
                </div>

                <div v-for="(row, index) in jenisdoks.data" :key="row.id"
                  class="transition-shadow duration-200 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md">
                  <div class="p-4">
                    <div class="flex items-start justify-between">
                      <div class="flex items-center flex-1">
                        <div
                          class="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-full">
                          <i class="text-indigo-600 fa-solid fa-file"></i>
                        </div>
                        <div class="flex-1 min-w-0 ml-3">
                          <div class="flex items-center space-x-2">
                            <span
                              class="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full">
                              #{{ index + 1 }}
                            </span>
                          </div>
                          <h3 class="mt-1 text-sm font-medium text-gray-900 truncate">{{ row.nama }}</h3>
                          <p class="mt-1 text-xs text-gray-500">{{ row.deskripsi }}</p>
                        </div>
                      </div>
                    </div>

                    <!-- Mobile Action Buttons -->
                    <div class="flex mt-4 space-x-2">
                      <button @click="editJenisDokumen(row)"
                        class="inline-flex items-center justify-center flex-1 px-3 py-2 text-sm font-medium text-indigo-700 transition-colors duration-200 border border-indigo-200 rounded-lg bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                        <i class="mr-1 fa-solid fa-edit"></i>
                        Edit
                      </button>
                      <button @click="deleteJenisDokumen(row.id, row.nama)"
                        class="inline-flex items-center justify-center flex-1 px-3 py-2 text-sm font-medium text-red-700 transition-colors duration-200 border border-red-200 rounded-lg bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                        <i class="mr-1 fa-solid fa-trash"></i>
                        Hapus
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Pagination -->
              <div class="mt-6" v-if="jenisdoks.data && jenisdoks.data.length > 0">
                <!-- Desktop Pagination -->
                <div class="hidden sm:flex sm:justify-center">
                  <Pagination :data="jenisdoks" :updatedPageNumber="updatedPageNumber" />
                </div>
                <!-- Mobile Pagination -->
                <div class="sm:hidden">
                  <div class="flex items-center justify-between px-2">
                    <button
                      @click="updatedPageNumber(jenisdoks.prev_page_url ? { url: jenisdoks.prev_page_url } : null)"
                      :disabled="!jenisdoks.prev_page_url"
                      class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 transition-colors duration-200 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
                      <i class="mr-1 fa-solid fa-chevron-left"></i>
                      Prev
                    </button>
                    <div class="flex items-center space-x-2">
                      <span class="text-sm text-gray-700">Hal {{ jenisdoks.current_page }} dari {{ jenisdoks.last_page
                        }}</span>
                    </div>
                    <button
                      @click="updatedPageNumber(jenisdoks.next_page_url ? { url: jenisdoks.next_page_url } : null)"
                      :disabled="!jenisdoks.next_page_url"
                      class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 transition-colors duration-200 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
                      Next
                      <i class="ml-1 fa-solid fa-chevron-right"></i>
                    </button>
                  </div>
                  <div class="flex justify-center mt-3" v-if="jenisdoks.last_page > 1">
                    <div class="flex items-center space-x-1">
                      <button v-for="page in getMobilePageNumbers()" :key="page"
                        @click="updatedPageNumber({ url: `${route('jenisdokumen.index')}?page=${page}` })" :class="[
                          'w-8 h-8 text-xs font-medium rounded-full transition-colors duration-200',
                          page === jenisdoks.current_page
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
                        ]">
                        {{ page }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { useForm, usePage, router } from "@inertiajs/vue3";
import { ref, computed, watch } from "vue";
import Swal from "sweetalert2";

import Pagination from "@/Components/Pagination.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Header from "@/Components/Header.vue";
import Container from "@/Layouts/Container.vue";

// Props
const props = defineProps({
  jenisdoks: {
    type: Object,
    required: true,
  },
});

// Search & pagination state
const search = ref(usePage().props.search || "");
const pageNumber = ref(1);

const listUrl = computed(() => {
  let url = new URL(route("jenisdokumen.index"));
  url.searchParams.set("page", pageNumber.value);
  if (search.value) url.searchParams.set("search", search.value);
  return url;
});

const updatedPageNumber = (link) => {
  if (!link || !link.url) return;
  const params = new URLSearchParams(link.url.split("?")[1] || "");
  const page = params.get("page");
  if (page) pageNumber.value = parseInt(page);
};

watch(
  () => listUrl.value,
  (url) => {
    router.visit(url.toString(), {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    });
  }
);

watch(
  () => search.value,
  (val) => {
    if (val) pageNumber.value = 1;
  }
);

// Form state
const form = useForm({
  id: null,
  nama: "",
  deskripsi: "",
});

// Helpers
const resetForm = () => {
  form.reset();
  form.clearErrors();
};

// Submit (create/update)
const submitForm = () => {
  const isEdit = !!form.id;
  const routeName = isEdit ? "jenisdokumen.update" : "jenisdokumen.store";
  form[isEdit ? "put" : "post"](route(routeName, isEdit ? form.id : {}), {
    onSuccess: () => {
      Swal.fire({
        icon: "success",
        title: isEdit ? "Berhasil Diperbarui!" : "Berhasil Ditambahkan!",
        text: `Jenis Dokumen "${form.nama}" berhasil ${isEdit ? "diperbarui" : "ditambahkan"}.`,
        timer: 3000,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
        timerProgressBar: true,
        showCloseButton: true,
      });
      resetForm();
    },
    onError: () => {
      Swal.fire({
        icon: "error",
        title: isEdit ? "Gagal Memperbarui!" : "Gagal Menambahkan!",
        text: "Terjadi kesalahan. Silakan periksa data dan coba lagi.",
        confirmButtonText: "OK",
        confirmButtonColor: "#dc3545",
      });
    },
  });
};

// Edit
const editJenisDokumen = (row) => {
  form.id = row.id;
  form.nama = row.nama;
  form.deskripsi = row.deskripsi;
  const formEl = document.querySelector("#jenis-dokumen-form");
  if (formEl) formEl.scrollIntoView({ behavior: "smooth", block: "center" });
};

// Cancel edit
const cancelEdit = () => resetForm();

// Delete
const deleteJenisDokumen = async (id, nama) => {
  const result = await Swal.fire({
    title: "Konfirmasi Hapus",
    html: `Apakah Anda yakin ingin menghapus <b>${nama}</b>?`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc3545",
    cancelButtonColor: "#6c757d",
    confirmButtonText: "Ya, Hapus!",
    cancelButtonText: "Batal",
    reverseButtons: true,
  });
  if (result.isConfirmed) {
    const delForm = useForm({});
    delForm.delete(route("jenisdokumen.destroy", id), {
      onSuccess: () => {
        Swal.fire({
          icon: "success",
          title: "Berhasil Dihapus!",
          text: "Jenis dokumen berhasil dihapus.",
          timer: 2500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
          timerProgressBar: true,
          showCloseButton: true,
        });
      },
      onError: () => {
        Swal.fire({
          icon: "error",
          title: "Gagal Menghapus!",
          text: "Terjadi kesalahan saat menghapus data.",
          confirmButtonText: "OK",
          confirmButtonColor: "#dc3545",
        });
      },
    });
  }
};

// Mobile pagination numbers
const getMobilePageNumbers = () => {
  const current = props.jenisdoks.current_page;
  const last = props.jenisdoks.last_page;
  const pages = [];
  if (last <= 5) {
    for (let i = 1; i <= last; i++) pages.push(i);
  } else if (current <= 3) {
    for (let i = 1; i <= 5; i++) pages.push(i);
  } else if (current >= last - 2) {
    for (let i = last - 4; i <= last; i++) pages.push(i);
  } else {
    for (let i = current - 2; i <= current + 2; i++) pages.push(i);
  }
  return pages;
};
</script>
